/**
 * The bio interior-linking anchor scheme — the single source of truth for how a
 * linking-tree node path maps to a DOM anchor and a URL fragment.
 *
 * A "link node path" is the canonical, leading-slash-free tree path of a target
 * (`custom-buttons/<id>`, `my-store`, `my-life/series/<id>`, …). It is used
 * verbatim as:
 *   - the `data-bio-link` attribute a scrollable section stamps on its element,
 *   - the URL hash fragment (`#<path>`) that addresses that section.
 *
 * Keeping one normalizer means the anchor a section renders and the fragment a
 * link builds always agree.
 */

/** Attribute a scrollable bio section stamps to mark itself as a link target. */
export const LINK_NODE_ATTR = 'data-bio-link';

/** The bio page's scroll container (see `ShellFrame`). Anchor lookups scope to it. */
export const BIO_SCROLL_ROOT_SELECTOR = '[data-bio-scroll-root]';

/**
 * Anchor path for the content-tabs section (strip + active tab body). Not a real
 * tree node — a synthetic marker the content section stamps so a cold/hard load
 * of a tab route (`/store` · `/life` · `/feed`) can implicitly jump the visitor
 * down to the content, past the profile header. See `useBioHashScroll`.
 */
export const CONTENT_TABS_ANCHOR_PATH = 'content-tabs';

/** Normalize a tree path / fullPath to the canonical leading-slash-free form. */
export function normalizeLinkNodePath(path: string): string {
  return path.replace(/^\/+/, '');
}

/** `{ 'data-bio-link': '<path>' }` for spreading onto a section's element. */
export function linkNodeAttr(path: string): Record<string, string> {
  return { [LINK_NODE_ATTR]: normalizeLinkNodePath(path) };
}

/** CSS selector matching the anchor element for `path`, scoped to the scroll root. */
export function linkNodeSelector(path: string): string {
  const normalized = normalizeLinkNodePath(path);
  return `${BIO_SCROLL_ROOT_SELECTOR} [${LINK_NODE_ATTR}="${CSS.escape(normalized)}"]`;
}

/** The URL hash fragment (including `#`) addressing `path`. */
export function linkNodeHash(path: string): string {
  return `#${normalizeLinkNodePath(path)}`;
}

/** The link node path carried by a `#<path>` fragment, or undefined if empty. */
export function linkNodePathFromHash(hash: string): string | undefined {
  // Take the LAST '#'-delimited segment: Next's App Router can re-append the
  // fragment when navigating (via <Link>) to an <Activity>-cached route that
  // already carried it, yielding "#path#path". A link node path never contains
  // '#', so the last non-empty segment is always the intended path.
  const segments = hash.split('#').filter(segment => segment.length > 0);
  const last = segments[segments.length - 1];
  if (last == null) {
    return undefined;
  }
  const path = normalizeLinkNodePath(last);
  return path.length > 0 ? decodeURIComponent(path) : undefined;
}
